import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Stack,
    Chip,
    Link,
    Tooltip,
} from '@mui/material';
import {
    LinkOff as LinkOffIcon,
    CheckCircle as CheckCircleIcon,
    SyncAlt as SyncAltIcon,
    OpenInNew as OpenInNewIcon,
    VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { getDuelsSyncStatus, connectDuelsSync, disconnectDuelsSync } from '../../services/duels-sync-service';
import DuelSyncImportWizard from './DuelSyncImportWizard';

function DuelSyncSettings() {
    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [token, setToken] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [importOpen, setImportOpen] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const s = await getDuelsSyncStatus();
            setStatus(s);
        } catch {
            setStatus(null);
        } finally {
            setLoadingStatus(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const handleConnect = async () => {
        if (!token.trim()) {
            setMessage({ type: 'error', text: 'Wklej token z duels.ink.' });
            return;
        }
        setConnecting(true);
        setMessage({ type: '', text: '' });
        try {
            await connectDuelsSync(token.trim());
            setToken('');
            setMessage({ type: 'success', text: 'Połączono z duels.ink!' });
            await loadStatus();
        } catch (err) {
            const text = err?.message?.includes('422')
                ? 'Nieprawidłowy token. Sprawdź Settings → API Tokens na duels.ink.'
                : 'Błąd połączenia. Spróbuj ponownie.';
            setMessage({ type: 'error', text });
        } finally {
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        setMessage({ type: '', text: '' });
        try {
            await disconnectDuelsSync();
            setMessage({ type: 'info', text: 'Odłączono od duels.ink.' });
            await loadStatus();
        } catch {
            setMessage({ type: 'error', text: 'Błąd. Spróbuj ponownie.' });
        } finally {
            setDisconnecting(false);
        }
    };

    const handleImportDone = async () => {
        setImportOpen(false);
        await loadStatus();
    };

    const formatDate = (iso) => {
        if (!iso) return null;
        return new Date(iso).toLocaleString('pl-PL', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const totalPending = (status?.pendingCount || 0) + (status?.duplicateCount || 0);

    return (
        <Box>
            <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
            >
                <SyncAltIcon />
                duels.ink
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Automatyczny import historii meczy z symulatora{' '}
                <Link
                    href="https://duels.ink"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
                >
                    duels.ink <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Link>
                .
            </Typography>

            {loadingStatus ? (
                <CircularProgress size={20} />
            ) : status?.connected ? (
                /* ── Connected state ── */
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            label="Połączono"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                color: '#4ade80',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                '& .MuiChip-icon': { color: '#4ade80' },
                            }}
                        />
                        {status.lastSyncedAt && (
                            <Typography variant="caption" color="text.secondary">
                                Ostatni sync: {formatDate(status.lastSyncedAt)}
                            </Typography>
                        )}
                    </Stack>

                    {totalPending > 0 && (
                        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                            {status.pendingCount > 0 && (
                                <span>{status.pendingCount} mecz{status.pendingCount !== 1 ? 'ów' : ''} czeka na przypisanie talii. </span>
                            )}
                            {status.duplicateCount > 0 && (
                                <span>{status.duplicateCount} potencjalnych duplikat{status.duplicateCount !== 1 ? 'ów' : ''}. </span>
                            )}
                            Przejdź do zakładki <strong>duels.ink</strong> w trackerze.
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<SyncAltIcon />}
                            onClick={() => setImportOpen(true)}
                            sx={{
                                backgroundColor: 'rgba(124, 58, 237, 0.8)',
                                '&:hover': { backgroundColor: '#7c3aed' },
                                textTransform: 'none',
                            }}
                        >
                            Importuj mecze
                        </Button>
                        <Tooltip title="Usuwa token i czyści kolejkę oczekujących">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={disconnecting ? <CircularProgress size={14} /> : <LinkOffIcon />}
                                onClick={handleDisconnect}
                                disabled={disconnecting}
                                sx={{ textTransform: 'none', borderColor: 'rgba(239, 68, 68, 0.5)', color: '#f87171' }}
                            >
                                Odłącz
                            </Button>
                        </Tooltip>
                    </Stack>
                </Box>
            ) : (
                /* ── Not connected state ── */
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Aby połączyć, wygeneruj token API na{' '}
                        <Link href="https://duels.ink/settings" target="_blank" rel="noopener noreferrer">
                            duels.ink → Settings → API Tokens
                        </Link>{' '}
                        i wklej go poniżej.
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        label="Token API (duels.ink)"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="Wklej token..."
                        disabled={connecting}
                        type="password"
                        InputProps={{
                            startAdornment: <VpnKeyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />,
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleConnect}
                        disabled={connecting || !token.trim()}
                        startIcon={connecting ? <CircularProgress size={14} /> : <SyncAltIcon />}
                        sx={{
                            backgroundColor: 'rgba(124, 58, 237, 0.8)',
                            '&:hover': { backgroundColor: '#7c3aed' },
                            textTransform: 'none',
                        }}
                    >
                        {connecting ? 'Łączenie...' : 'Połącz z duels.ink'}
                    </Button>
                </Box>
            )}

            {message.text && (
                <Alert severity={message.type || 'info'} sx={{ mt: 2, py: 0.5 }} onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            <DuelSyncImportWizard
                open={importOpen}
                onClose={handleImportDone}
                lastSyncedAt={(() => {
                    const ls = status?.lastSyncedAt ? new Date(status.lastSyncedAt).getTime() : 0;
                    const ni = status?.newestImportedAt ? new Date(status.newestImportedAt).getTime() : 0;
                    const best = Math.max(ls, ni);
                    return best > 0 ? new Date(best).toISOString() : null;
                })()}
            />
        </Box>
    );
}

export default DuelSyncSettings;
